"use client";

/**
 * Client-side Analytics Tracking
 *
 * Safe, non-blocking tracking functions for use in React components.
 * All functions are wrapped in try-catch to never affect user experience.
 */

import posthog from "posthog-js";
import { AnalyticsEvents } from "./events";
import type {
  AnalyticsEventMap,
  EventName,
  // UI Events
  PlanCtaClickedProps,
  PlanViewedProps,
  CheckoutStartedProps,
  ProgramViewedProps,
  WorkoutDayViewedProps,
  RecipeViewedProps,
  RecipeFilterAppliedProps,
  CourseViewedProps,
  LessonStartedProps,
  VideoStartedProps,
  VideoProgressProps,
  VideoCompletedProps,
} from "./types";

/**
 * Generic client-side tracking function
 * Wrapped in try-catch to never throw errors that affect the user
 */
function trackClient<E extends EventName>(
  event: E,
  properties: AnalyticsEventMap[E],
): void {
  try {
    if (typeof window === "undefined" || !posthog.__loaded) return;

    posthog.capture(event, properties as Record<string, unknown>);
  } catch (error) {
    // Silently fail - analytics should never break the app
    console.error(`[Analytics] Failed to track ${event}:`, error);
  }
}

// ===========================================
// CONVERSION EVENTS
// ===========================================

export function trackPlanCtaClicked(props: PlanCtaClickedProps): void {
  trackClient(AnalyticsEvents.PLAN_CTA_CLICKED, props);
}

export function trackPlanViewed(props: PlanViewedProps): void {
  trackClient(AnalyticsEvents.PLAN_VIEWED, props);
}

export function trackCheckoutStarted(props: CheckoutStartedProps): void {
  trackClient(AnalyticsEvents.CHECKOUT_STARTED, props);
}

// ===========================================
// WORKOUT UI EVENTS
// ===========================================

export function trackProgramViewed(props: ProgramViewedProps): void {
  trackClient(AnalyticsEvents.PROGRAM_VIEWED, props);
}

export function trackWorkoutDayViewed(props: WorkoutDayViewedProps): void {
  trackClient(AnalyticsEvents.WORKOUT_DAY_VIEWED, props);
}

// ===========================================
// RECIPE UI EVENTS
// ===========================================

export function trackRecipeViewed(props: RecipeViewedProps): void {
  trackClient(AnalyticsEvents.RECIPE_VIEWED, props);
}

export function trackRecipeFilterApplied(
  props: RecipeFilterAppliedProps,
): void {
  trackClient(AnalyticsEvents.RECIPE_FILTER_APPLIED, props);
}

// ===========================================
// COURSE UI EVENTS
// ===========================================

export function trackCourseViewed(props: CourseViewedProps): void {
  trackClient(AnalyticsEvents.COURSE_VIEWED, props);
}

export function trackLessonStarted(props: LessonStartedProps): void {
  trackClient(AnalyticsEvents.LESSON_STARTED, props);
}

export function trackVideoStarted(props: VideoStartedProps): void {
  trackClient(AnalyticsEvents.VIDEO_STARTED, props);
}

export function trackVideoProgress(props: VideoProgressProps): void {
  trackClient(AnalyticsEvents.VIDEO_PROGRESS, props);
}

export function trackVideoCompleted(props: VideoCompletedProps): void {
  trackClient(AnalyticsEvents.VIDEO_COMPLETED, props);
}
